import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useIeKitLabels } from '@/hooks/useIeKitLabels';
import { PAGE_SIZE_OPTIONS } from '@/constants/pagination';
import { groupBy, uniq } from 'es-toolkit';
import { logger } from '@equip/utils/logger';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  Popover,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  Search as SearchIcon,
  ViewColumn as ViewColumnIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { DataGrid, type GridColumnVisibilityModel } from '@mui/x-data-grid';
import type { GridSortModel } from '@mui/x-data-grid';
import {
  useIESummary,
  useCatalogChildren,
  useConvertAllocationToSizedAsset,
  useConvertAllocationToSizedAssetBatch,
  useConvertThrToPhr,
  useEntities,
  useExtendThrDueDate,
  usePHRItems,
  useTHRItems,
} from '@/hooks';
import { useIemFrontDoorStore } from '@/stores/iemFrontDoorStore';
import type { ThrExtensionInfo } from '@/services/iem';
import { useAuth } from '@/hooks/useAuth';
import { useFrontWindowAllocationPreferences } from '@/hooks/useFrontWindowPreferences';
import {
  getDefaultAllocationSortModel,
  type CatalogType,
  type RecoverableFilter,
  type ReceiptFilter,
} from '@/stores/frontWindowPreferencesStore';
import {
  useColumnPreferencesStore,
  type ColumnVisibilityModel,
} from '@/stores/useColumnPreferencesStore';
import { useNotifications } from '@/stores/useNotificationStore';
import { createAllocationColumns } from '@/components/iem/shared/columns';
import { UpdateThrItemDialog } from '@/components/iem/dialogs/UpdateThrItemDialog';
import { TableSkeleton } from '@/components/iem/tables/TableSkeleton';
import { runAllocationSummaryReport } from '@/reports/runners/allocationSummaryRunner';
import type { AllocationSummaryMemberInfo } from '@/reports/templates/AllocationSummaryTemplate';
import {
  resolvePrintHeading,
  buildFiltersSummary,
  formatCatalogType,
} from './allocationPrintUtils';
import { useTabActions } from '@/components/iem/panels/TabActionsContext';
import { openDocumentWithAuth, preOpenWindow } from '@/utils/pdfHelpers';
import { multiWordMatch } from '@/utils/multiWordMatch';
import { usePersonLabels } from '@/hooks/usePersonLabels';

type AllocationStatusKind = 'compliant' | 'shortage' | 'overage' | 'unauthorized' | 'unknown';

interface AllocationRow {
  id: string;
  source: 'phr' | 'thr';
  item: string;
  lin: string;
  nsn: string;
  catalogId?: string | null;
  parentCatalogId?: string | null;
  handReceipt: 'PHR' | 'THR' | 'PnP';
  issueDate: string;
  dueDate: string;
  expirationDate?: string | null;
  serialNumber?: string | null;
  quantity: number;
  cost: number | null;
  status: string;
  statusKind: AllocationStatusKind;
  authorizedQty: number | null;
  issuedQty: number | null;
  isOverdue: boolean;
  authorizationMatchKey: string | null;
  recoverable: boolean | null;
  nonrecoverableReason?: string | null;
  catalogType: string;
  assetId?: string | null;
  personId?: string | null;
  unauthorized?: boolean;
  extension: ThrExtensionInfo | null;
  hasExtension: boolean;
  itemIds: string[];
  ieKitId?: string | null;
  kitName?: string | null;
}

const FRONT_WINDOW_ALLOCATION_PREF_KEY = 'front-window-allocation-columns';
const ALLOCATION_MANAGEABLE_COLUMNS = [
  { field: 'kitName', label: 'IE Kit' }, // overridden at runtime
  { field: 'lin', label: 'LIN' },
  { field: 'nsn', label: 'NSN' },
  { field: 'quantity', label: 'Qty' },
  { field: 'cost', label: 'Cost' },
  { field: 'handReceipt', label: 'Hand Receipt (HR)' },
  { field: 'issueDate', label: 'Issue Date' },
  { field: 'dueDate', label: 'Due / Return' },
  { field: 'expirationDate', label: 'Expiration Date' },
  { field: 'status', label: 'Status' },
  { field: 'catalogType', label: 'Type' },
  { field: 'recoverable', label: 'Recoverable' },
] as const;

type AllocationManageableField = (typeof ALLOCATION_MANAGEABLE_COLUMNS)[number]['field'];

const buildAllocationVisibilityModel = (
  model?: ColumnVisibilityModel,
): GridColumnVisibilityModel => {
  const next: GridColumnVisibilityModel = {};
  ALLOCATION_MANAGEABLE_COLUMNS.forEach(({ field }) => {
    next[field] = model?.[field] !== false;
  });
  return next;
};

const allocationModelsEqual = (a: GridColumnVisibilityModel, b: GridColumnVisibilityModel) =>
  ALLOCATION_MANAGEABLE_COLUMNS.every(({ field }) => a[field] === b[field]);

const statusLabelByKind: Record<AllocationStatusKind, string> = {
  compliant: 'Compliant',
  shortage: 'Shortage',
  overage: 'Overage',
  unauthorized: 'Unauthorized',
  unknown: 'Unknown',
};

export const AllocationTab: React.FC = () => {
  const { currentCustomer } = useIemFrontDoorStore();
  const { user, isViewer } = useAuth();
  const tabActions = useTabActions();
  const [phrErrorDismissed, setPhrErrorDismissed] = useState(false);
  const [thrErrorDismissed, setThrErrorDismissed] = useState(false);
  const { error: notifyError, warning: notifyWarning } = useNotifications();
  const person = usePersonLabels('front_window');
  const { columnLabel: ieKitLabel } = useIeKitLabels('front_window');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAllocation, setSelectedAllocation] = useState<AllocationRow | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [columnSelectorAnchor, setColumnSelectorAnchor] = useState<HTMLElement | null>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const printingRef = useRef(false);
  const gridWrapperRef = useRef<HTMLDivElement>(null);

  const extendThrDueDate = useExtendThrDueDate();
  const convertThrToPhr = useConvertThrToPhr();
  const convertAllocationToSizedAsset = useConvertAllocationToSizedAsset();
  const convertAllocationToSizedAssetBatch = useConvertAllocationToSizedAssetBatch();
  const { preferences: allocationPrefs, updatePreferences: updateAllocationPrefs } =
    useFrontWindowAllocationPreferences(user?.id);

  const receiptFilter: ReceiptFilter = allocationPrefs.receiptFilter;
  const typeFilters: CatalogType[] = allocationPrefs.typeFilters;
  const recoverableFilter: RecoverableFilter = allocationPrefs.recoverableFilter;
  const search = allocationPrefs.search;
  const sortModel = allocationPrefs.sortModel;

  const personId = currentCustomer?.id || '';
  const effectiveUserId = user?.id ?? 'anonymous';

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>(
    () => {
      const stored = useColumnPreferencesStore
        .getState()
        .getVisibility(effectiveUserId, FRONT_WINDOW_ALLOCATION_PREF_KEY);
      return buildAllocationVisibilityModel(stored);
    },
  );

  const {
    data: phrItems,
    isLoading: phrLoading,
    error: phrError,
    isFetched: phrFetched,
  } = usePHRItems(personId, { status: 'neq.closed' });
  const {
    data: thrItems,
    isLoading: thrLoading,
    error: thrError,
    isFetched: thrFetched,
  } = useTHRItems(personId, { status: 'neq.closed' });

  const catalogIdsForCost = useMemo(() => {
    const ids = new Set<string>();

    for (const item of phrItems ?? []) {
      if (item.catalogId) ids.add(item.catalogId);
      if (item.parentCatalogId) ids.add(item.parentCatalogId);
    }

    for (const item of thrItems ?? []) {
      if (item.catalogId) ids.add(item.catalogId);
      if (item.parentCatalogId) ids.add(item.parentCatalogId);
    }

    return Array.from(ids).sort();
  }, [phrItems, thrItems]);

  const catalogIdFilter = useMemo(() => {
    if (catalogIdsForCost.length === 0) return null;
    if (catalogIdsForCost.length === 1) return `eq.${catalogIdsForCost[0]}`;
    return `in.(${catalogIdsForCost.join(',')})`;
  }, [catalogIdsForCost]);

  const { data: catalogsForCost = [] } = useEntities({
    type: 'catalog',
    limit: Math.max(catalogIdsForCost.length, 100),
    filters: catalogIdFilter ? { id: catalogIdFilter } : {},
    enabled: Boolean(catalogIdFilter),
  });

  const catalogUnitCostById = useMemo(() => {
    const map = new Map<string, number>();
    for (const catalog of catalogsForCost) {
      const unitPriceRaw = catalog.entity_data?.unit_price;
      const unitPrice =
        typeof unitPriceRaw === 'number'
          ? unitPriceRaw
          : typeof unitPriceRaw === 'string'
            ? Number.parseFloat(unitPriceRaw)
            : null;
      if (typeof unitPrice === 'number' && Number.isFinite(unitPrice)) {
        map.set(catalog.id, unitPrice);
      }
    }
    return map;
  }, [catalogsForCost]);

  useEffect(() => {
    if (!thrItems || thrItems.length === 0) {
      logger.debug('THR data empty or undefined', { component: 'AllocationTab' });
      return;
    }

    const rawSnapshot = thrItems.map((item) => ({
      handReceiptId: item.id,
      hasExtension: item.hasExtension,
      extension: item.extension ?? null,
    }));

    const extensionSnapshot = rawSnapshot.filter((row) => row.hasExtension || row.extension);

    logger.debug('THR snapshots loaded', {
      component: 'AllocationTab',
      rawCount: rawSnapshot.length,
      extensionCount: extensionSnapshot.length,
    });
  }, [thrItems]);
  const {
    data: ieData,
    isLoading: ieLoading,
    error: ieError,
    isFetched: ieFetched,
  } = useIESummary(personId);

  useEffect(() => {
    if (phrError) setPhrErrorDismissed(false);
  }, [phrError]);

  useEffect(() => {
    if (thrError) setThrErrorDismissed(false);
  }, [thrError]);

  useEffect(() => {
    if (ieError) {
      notifyError(
        `${ieKitLabel} Load Failed`,
        ieError instanceof Error ? ieError.message : 'Unknown error',
      );
    }
  }, [ieError, notifyError]);

  useEffect(() => {
    setDialogOpen(false);
    setSelectedAllocation(null);
    setProcessingId(null);
  }, [currentCustomer?.id]);

  useEffect(() => {
    const stored = useColumnPreferencesStore
      .getState()
      .getVisibility(effectiveUserId, FRONT_WINDOW_ALLOCATION_PREF_KEY);
    setColumnVisibilityModel(buildAllocationVisibilityModel(stored));
  }, [effectiveUserId]);

  useEffect(() => {
    updateAllocationPrefs({ columnVisibility: columnVisibilityModel });
  }, [columnVisibilityModel, updateAllocationPrefs]);

  const allocationRows = useMemo<AllocationRow[]>(() => {
    const resolveUnitCost = ({
      catalogId,
      parentCatalogId,
    }: {
      catalogId?: string | null;
      parentCatalogId?: string | null;
    }): number | null => {
      if (catalogId && catalogUnitCostById.has(catalogId)) {
        return catalogUnitCostById.get(catalogId) ?? null;
      }
      if (parentCatalogId && catalogUnitCostById.has(parentCatalogId)) {
        return catalogUnitCostById.get(parentCatalogId) ?? null;
      }
      return null;
    };

    const isNotAuthorizedStatus = (status?: string | null) => {
      const normalized = (status ?? '').trim().toLowerCase();
      return normalized.startsWith('not authorized') || normalized.startsWith('unauthorized');
    };

    const normalizeMatchKey = (value?: string | null) => (value ?? '').trim().toLowerCase();
    const toAuthorizationKey = (kind: 'catalog' | 'lin', value: string) => `${kind}:${value}`;
    const toNumber = (value: unknown): number => {
      if (typeof value === 'number' && Number.isFinite(value)) return value;
      if (typeof value === 'string') {
        const parsed = Number.parseFloat(value);
        if (Number.isFinite(parsed)) return parsed;
      }
      return 0;
    };
    const isOverdueDate = (dueDate?: string | null, status?: string | null): boolean => {
      const statusLower = (status ?? '').trim().toLowerCase();
      if (statusLower.includes('overdue')) return true;
      const normalized = (dueDate ?? '').trim();
      if (!normalized || normalized === '--' || normalized === 'Multiple') return false;
      const parsed = new Date(normalized);
      if (Number.isNaN(parsed.getTime())) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      parsed.setHours(0, 0, 0, 0);
      return parsed.getTime() < today.getTime();
    };

    const authorizedCatalogIdSet: Set<string> = new Set();
    const authorizedQtyByMatchKey = new Map<string, number>();

    const ieKitNameMap = new Map<string, string>();
    const ieMembershipKeys = new Set<string>();

    const toMembershipKey = (ieKitId: string, kind: 'catalog' | 'lin', value: string) =>
      `${ieKitId}|${kind}|${value}`;

    (ieData ?? []).forEach((b) => {
      if (isNotAuthorizedStatus(b.status)) return;

      const normalizedKitId = normalizeMatchKey(b.ieKitId);
      const normalizedCatalogId = normalizeMatchKey(b.catalogId);
      const authorizedQty = toNumber(b.authorizedQty);

      if (normalizedCatalogId) {
        authorizedCatalogIdSet.add(normalizedCatalogId);
        const key = toAuthorizationKey('catalog', normalizedCatalogId);
        authorizedQtyByMatchKey.set(key, (authorizedQtyByMatchKey.get(key) ?? 0) + authorizedQty);
      }

      if (!normalizedKitId) return;

      if (b.kitName) {
        ieKitNameMap.set(normalizedKitId, b.kitName);
      }

      if (normalizedCatalogId) {
        ieMembershipKeys.add(toMembershipKey(normalizedKitId, 'catalog', normalizedCatalogId));
      }
    });

    const resolveAuthorizationMatchKey = ({
      catalogId,
      parentCatalogId,
    }: {
      catalogId?: string | null;
      parentCatalogId?: string | null;
    }): string | null => {
      const normalizedCatalogId = normalizeMatchKey(catalogId);
      if (normalizedCatalogId && authorizedCatalogIdSet.has(normalizedCatalogId)) {
        return toAuthorizationKey('catalog', normalizedCatalogId);
      }

      const normalizedParentCatalogId = normalizeMatchKey(parentCatalogId);
      if (normalizedParentCatalogId && authorizedCatalogIdSet.has(normalizedParentCatalogId)) {
        return toAuthorizationKey('catalog', normalizedParentCatalogId);
      }

      return null;
    };

    const resolveKitNameIfMember = ({
      ieKitId,
      catalogId,
      parentCatalogId,
    }: {
      ieKitId?: string | null;
      catalogId?: string | null;
      parentCatalogId?: string | null;
    }): string | null => {
      const normalizedCatalogId = normalizeMatchKey(catalogId);
      const normalizedParentCatalogId = normalizeMatchKey(parentCatalogId);

      const tryKit = (kitId: string): string | null => {
        if (
          normalizedCatalogId &&
          ieMembershipKeys.has(toMembershipKey(kitId, 'catalog', normalizedCatalogId))
        ) {
          return ieKitNameMap.get(kitId) ?? null;
        }
        if (
          normalizedParentCatalogId &&
          ieMembershipKeys.has(toMembershipKey(kitId, 'catalog', normalizedParentCatalogId))
        ) {
          return ieKitNameMap.get(kitId) ?? null;
        }
        return null;
      };

      const normalizedKitId = normalizeMatchKey(ieKitId);

      // If ie_kit_id references a kit still assigned to this person, use strict matching.
      if (normalizedKitId && ieKitNameMap.has(normalizedKitId)) {
        return tryKit(normalizedKitId);
      }

      // ie_kit_id is missing or stale (references a kit no longer assigned).
      // Fall back to scanning all assigned kits for catalog/LIN membership.
      for (const kitId of ieKitNameMap.keys()) {
        const result = tryKit(kitId);
        if (result) return result;
      }

      return null;
    };

    // Only evaluate IE Kit-based unauthorized rules after IE Kit has successfully loaded.
    // This avoids temporarily flagging everything while IE Kit is still loading or unavailable.
    const ieReady = ieFetched && !ieError;

    const phrRows = (phrItems ?? []).map((item) => {
      const statusLower = (item.status ?? '').toLowerCase();
      const authorizationMatchKey = resolveAuthorizationMatchKey({
        catalogId: item.catalogId,
        parentCatalogId: item.parentCatalogId,
      });
      const kitName = resolveKitNameIfMember({
        ieKitId: item.ie_kit_id,
        catalogId: item.catalogId,
        parentCatalogId: item.parentCatalogId,
      });

      const unauthorized =
        statusLower.includes('unauthorized') ||
        (ieReady && !kitName && !authorizationMatchKey && item.type !== 'pnp');

      const cost = resolveUnitCost({
        catalogId: item.catalogId,
        parentCatalogId: item.parentCatalogId,
      });
      const catalogType = formatCatalogType(item.type);

      return {
        id: item.id,
        source: 'phr' as const,
        item: item.item,
        lin: item.lin ?? '',
        nsn: item.nsn,
        catalogId: item.catalogId ?? null,
        parentCatalogId: item.parentCatalogId ?? null,
        handReceipt: item.type === 'pnp' ? ('PnP' as const) : ('PHR' as const),
        issueDate: item.issueDate,
        dueDate: '--',
        expirationDate: item.expirationDate ?? null,
        serialNumber: item.serial_number ?? null,
        quantity: 1,
        cost,
        status: item.status ?? 'Unknown',
        statusKind: 'unknown' as const,
        authorizedQty: null,
        issuedQty: null,
        isOverdue: false,
        authorizationMatchKey,
        recoverable: item.recoverable ?? null,
        nonrecoverableReason: null,
        catalogType,
        assetId: item.asset_id ?? null,
        personId: item.person_id ?? null,
        unauthorized,
        extension: item.extension ?? null,
        hasExtension: item.hasExtension,
        itemIds: [item.id],
        ieKitId: item.ie_kit_id,
        kitName,
      } satisfies AllocationRow;
    });

    const thrRows = (thrItems ?? []).map((item) => {
      const nonrecoverableReason = item.nonrecoverable_reason ?? null;
      const statusLower = (item.status ?? '').toLowerCase();
      const authorizationMatchKey = resolveAuthorizationMatchKey({
        catalogId: item.catalogId,
        parentCatalogId: item.parentCatalogId,
      });
      const kitName = resolveKitNameIfMember({
        ieKitId: item.ie_kit_id,
        catalogId: item.catalogId,
        parentCatalogId: item.parentCatalogId,
      });

      const unauthorized =
        statusLower.includes('unauthorized') ||
        (ieReady && !kitName && !authorizationMatchKey && item.type !== 'pnp');

      const cost = resolveUnitCost({
        catalogId: item.catalogId,
        parentCatalogId: item.parentCatalogId,
      });
      const catalogType = formatCatalogType(item.type);

      return {
        id: item.id,
        source: 'thr' as const,
        item: item.item,
        lin: item.lin ?? '',
        nsn: item.nsn,
        catalogId: item.catalogId ?? null,
        parentCatalogId: item.parentCatalogId ?? null,
        handReceipt: 'THR' as const,
        issueDate: item.issueDate,
        dueDate: item.dueDate,
        expirationDate: item.expirationDate ?? null,
        serialNumber: null,
        quantity: item.qty,
        cost,
        status: item.status ?? 'Unknown',
        statusKind: 'unknown' as const,
        authorizedQty: null,
        issuedQty: null,
        isOverdue: isOverdueDate(item.dueDate, item.status),
        authorizationMatchKey,
        recoverable: item.recoverable ?? null,
        nonrecoverableReason,
        catalogType,
        assetId: item.asset_id ?? null,
        personId: item.person_id ?? null,
        unauthorized,
        extension: item.extension ?? null,
        hasExtension: item.hasExtension,
        itemIds: [item.id],
        ieKitId: item.ie_kit_id,
        kitName,
      } satisfies AllocationRow;
    });

    const combined = [...thrRows, ...phrRows];

    const issuedQtyByMatchKey = new Map<string, number>();
    combined.forEach((row) => {
      if (!row.authorizationMatchKey || row.unauthorized) {
        return;
      }
      issuedQtyByMatchKey.set(
        row.authorizationMatchKey,
        (issuedQtyByMatchKey.get(row.authorizationMatchKey) ?? 0) + row.quantity,
      );
    });

    const withCompliance = combined.map((row) => {
      if (row.unauthorized) {
        return {
          ...row,
          status: statusLabelByKind.unauthorized,
          statusKind: 'unauthorized' as const,
          authorizedQty: null,
          issuedQty: null,
        };
      }

      if (!ieReady || !row.authorizationMatchKey) {
        return {
          ...row,
          status: statusLabelByKind.unknown,
          statusKind: 'unknown' as const,
          authorizedQty: null,
          issuedQty: null,
        };
      }

      const authorizedQty = authorizedQtyByMatchKey.get(row.authorizationMatchKey) ?? 0;
      const issuedQty = issuedQtyByMatchKey.get(row.authorizationMatchKey) ?? row.quantity;
      const statusKind: AllocationStatusKind =
        issuedQty > authorizedQty
          ? 'overage'
          : issuedQty < authorizedQty
            ? 'shortage'
            : 'compliant';

      return {
        ...row,
        status: statusLabelByKind[statusKind],
        statusKind,
        authorizedQty,
        issuedQty,
      };
    });

    const searchValue = search.trim().toLowerCase();

    const filtered = withCompliance.filter((row) => {
      if (receiptFilter === 'phr' && row.source !== 'phr') return false;
      if (receiptFilter === 'thr' && row.source !== 'thr') return false;
      if (
        typeFilters.length > 0 &&
        !typeFilters.includes(row.catalogType as CatalogType)
      )
        return false;
      if (recoverableFilter === 'recoverable' && row.recoverable !== true) return false;
      if (recoverableFilter === 'nonRecoverable' && row.recoverable !== false) return false;

      if (!searchValue) return true;
      const searchable = `${row.item} ${row.lin} ${row.nsn} ${row.serialNumber ?? ''} ${String(row.quantity)}`;
      return multiWordMatch(searchable, searchValue);
    });

    // Keep receipt types distinct and avoid collapsing different items that share an NSN.
    const grouped = groupBy(
      filtered,
      (row) =>
        `${row.source}:${row.handReceipt}:${row.catalogId ?? row.parentCatalogId ?? ''}:${row.lin}:${row.nsn}:${row.item.trim().toLowerCase()}`,
    );

    return Object.entries(grouped).map(([groupKey, rows]) => {
      if (rows.length === 1) {
        return rows[0]!;
      }

      const first = rows[0]!;
      const totalQty = rows.reduce((sum, r) => sum + r.quantity, 0);
      const itemIds = rows.flatMap((r) => r.itemIds);

      const uniqueIssueDates = uniq(rows.map((r) => r.issueDate).filter(Boolean));
      const uniqueDueDates = uniq(rows.map((r) => r.dueDate).filter((d) => d && d !== '--'));
      const uniqueSerials = uniq(rows.map((r) => r.serialNumber).filter(Boolean));
      const uniqueCatalogIds = uniq(rows.map((r) => r.catalogId).filter(Boolean));
      const uniqueParentCatalogIds = uniq(rows.map((r) => r.parentCatalogId).filter(Boolean));
      const uniqueCosts = uniq(
        rows
          .map((r) => r.cost)
          .filter((c): c is number => typeof c === 'number' && Number.isFinite(c)),
      );
      const uniqueIeKitIds = uniq(rows.map((r) => r.ieKitId).filter(Boolean));
      const uniqueKitNames = uniq(rows.map((r) => r.kitName).filter(Boolean));
      const uniqueExpirationDates = uniq(rows.map((r) => r.expirationDate).filter(Boolean));
      const uniqueAuthorizationMatchKeys = uniq(
        rows.map((r) => r.authorizationMatchKey).filter((value): value is string => Boolean(value)),
      );
      const groupedIeKitId = uniqueIeKitIds.length === 1 ? (uniqueIeKitIds[0] ?? null) : null;
      const groupedKitName =
        uniqueKitNames.length === 1 && rows.every((r) => r.kitName === uniqueKitNames[0])
          ? (uniqueKitNames[0] ?? null)
          : null;
      const groupedStatusKind: AllocationStatusKind = (() => {
        if (rows.some((r) => r.statusKind === 'unauthorized')) return 'unauthorized';
        if (rows.some((r) => r.statusKind === 'overage')) return 'overage';
        if (rows.some((r) => r.statusKind === 'shortage')) return 'shortage';
        if (rows.some((r) => r.statusKind === 'compliant')) return 'compliant';
        return 'unknown';
      })();
      const groupedAuthorizationMatchKey =
        uniqueAuthorizationMatchKeys.length === 1
          ? (uniqueAuthorizationMatchKeys[0] ?? null)
          : null;
      const groupedAuthorizedQty =
        groupedAuthorizationMatchKey && groupedStatusKind !== 'unauthorized'
          ? (authorizedQtyByMatchKey.get(groupedAuthorizationMatchKey) ?? null)
          : null;
      const groupedIssuedQty =
        groupedAuthorizationMatchKey && groupedStatusKind !== 'unauthorized'
          ? (issuedQtyByMatchKey.get(groupedAuthorizationMatchKey) ?? null)
          : null;

      return {
        ...first,
        id: `group:${groupKey}`,
        quantity: totalQty,
        itemIds,
        catalogId: uniqueCatalogIds.length === 1 ? (uniqueCatalogIds[0] ?? null) : first.catalogId,
        parentCatalogId:
          uniqueParentCatalogIds.length === 1 ? (uniqueParentCatalogIds[0] ?? null) : null,
        issueDate: uniqueIssueDates.length > 1 ? 'Multiple' : (uniqueIssueDates[0] ?? '--'),
        dueDate: uniqueDueDates.length > 1 ? 'Multiple' : (uniqueDueDates[0] ?? '--'),
        expirationDate:
          uniqueExpirationDates.length === 1 ? (uniqueExpirationDates[0] ?? null) : null,
        status: statusLabelByKind[groupedStatusKind],
        statusKind: groupedStatusKind,
        authorizedQty: groupedAuthorizedQty,
        issuedQty: groupedIssuedQty,
        isOverdue: rows.some((r) => r.isOverdue),
        authorizationMatchKey: groupedAuthorizationMatchKey,
        serialNumber: uniqueSerials.length > 1 ? 'Multiple' : (uniqueSerials[0] ?? null),
        cost: uniqueCosts.length === 1 ? (uniqueCosts[0] ?? null) : null,
        ieKitId: groupedIeKitId,
        kitName: groupedKitName,
        unauthorized: rows.some((r) => r.unauthorized),
      };
    });
  }, [
    phrItems,
    thrItems,
    ieData,
    ieFetched,
    ieError,
    search,
    receiptFilter,
    typeFilters,
    recoverableFilter,
    catalogUnitCostById,
  ]);

  // Sticky column headers: translate the header row when it scrolls past the viewport top
  useEffect(() => {
    const wrapper = gridWrapperRef.current;
    if (!wrapper) return;

    // Find the scrollable ancestor (MainLayout's overflow:auto Box)
    let scroller: HTMLElement | null = wrapper.parentElement;
    while (scroller && getComputedStyle(scroller).overflowY !== 'auto' && getComputedStyle(scroller).overflowY !== 'scroll') {
      scroller = scroller.parentElement;
    }
    if (!scroller) return;

    const handleScroll = () => {
      const topContainer = wrapper.querySelector<HTMLElement>('.MuiDataGrid-topContainer');
      if (!topContainer) return;

      const scrollerRect = scroller!.getBoundingClientRect();
      const wrapperRect = wrapper.getBoundingClientRect();
      const headerHeight = topContainer.offsetHeight;

      if (wrapperRect.top < scrollerRect.top && wrapperRect.bottom > scrollerRect.top + headerHeight) {
        const offset = scrollerRect.top - wrapperRect.top;
        topContainer.style.transform = `translateY(${offset}px)`;
        topContainer.style.position = 'relative';
        topContainer.style.zIndex = '3';
      } else {
        topContainer.style.transform = '';
        topContainer.style.position = '';
        topContainer.style.zIndex = '';
      }
    };

    scroller.addEventListener('scroll', handleScroll, { passive: true });
    return () => scroller!.removeEventListener('scroll', handleScroll);
  }, [allocationRows.length]);

  const performerId = user?.id;
  const selectedParentCatalogIdsForSizing = useMemo(() => {
    if (!dialogOpen || !selectedAllocation) {
      return [];
    }

    if (selectedAllocation.parentCatalogId) {
      return [];
    }

    const catalogId = selectedAllocation.catalogId?.trim();
    return catalogId ? [catalogId] : [];
  }, [dialogOpen, selectedAllocation]);

  const { data: selectedCatalogChildren = [], isLoading: selectedCatalogChildrenLoading } =
    useCatalogChildren({
      parentCatalogIds: selectedParentCatalogIdsForSizing,
      enabled: dialogOpen,
    });

  const sizeVariantsForDialog = useMemo(() => {
    const catalogId = selectedAllocation?.catalogId?.trim().toLowerCase();
    if (!catalogId) {
      return [];
    }

    return selectedCatalogChildren.filter(
      (option) => option.parentCatalogId.trim().toLowerCase() === catalogId,
    );
  }, [selectedAllocation?.catalogId, selectedCatalogChildren]);

  const handleOpenUpdate = useCallback(
    (handReceiptId: string) => {
      const row = allocationRows.find((entry) => entry.id === handReceiptId);
      if (!row) {
        notifyError('Allocation unavailable', 'Refresh the data and try again.');
        return;
      }

      setSelectedAllocation(row);
      setDialogOpen(true);
    },
    [notifyError, allocationRows],
  );

  const handleCloseDialog = useCallback(() => {
    if (
      extendThrDueDate.isPending ||
      convertThrToPhr.isPending ||
      convertAllocationToSizedAsset.isPending ||
      convertAllocationToSizedAssetBatch.isPending
    ) {
      return;
    }
    setDialogOpen(false);
    setSelectedAllocation(null);
    setProcessingId(null);
  }, [
    convertAllocationToSizedAsset.isPending,
    convertAllocationToSizedAssetBatch.isPending,
    convertThrToPhr.isPending,
    extendThrDueDate.isPending,
  ]);

  const handleExtend = useCallback(
    async ({
      handReceiptId,
      newDueDate,
      note,
    }: {
      handReceiptId: string;
      newDueDate: string;
      note?: string;
    }) => {
      setProcessingId(handReceiptId);
      try {
        const row = allocationRows.find((r) => r.id === handReceiptId);
        const idsToUpdate = row ? row.itemIds : [handReceiptId];

        await Promise.all(
          idsToUpdate.map((id) =>
            extendThrDueDate.mutateAsync({
              handReceiptId: id,
              newDueDate,
              performedBy: performerId,
              note,
            }),
          ),
        );
      } finally {
        setProcessingId(null);
      }
    },
    [extendThrDueDate, performerId, allocationRows],
  );

  const handleConvert = useCallback(
    async ({
      handReceiptId,
      approvalReference,
      approvalConfirmed,
      note,
    }: {
      handReceiptId: string;
      approvalReference?: string;
      approvalConfirmed: boolean;
      note?: string;
    }) => {
      setProcessingId(handReceiptId);
      try {
        const row = allocationRows.find((r) => r.id === handReceiptId);
        const idsToUpdate = row ? row.itemIds : [handReceiptId];

        await Promise.all(
          idsToUpdate.map((id) =>
            convertThrToPhr.mutateAsync({
              handReceiptId: id,
              approvalReference,
              performedBy: performerId,
              approvalConfirmed,
              note,
            }),
          ),
        );
      } finally {
        setProcessingId(null);
      }
    },
    [convertThrToPhr, performerId, allocationRows],
  );

  const handleConvertToSizedAsset = useCallback(
    async ({
      handReceiptId,
      targetCatalogId,
      justification,
    }: {
      handReceiptId: string;
      targetCatalogId: string;
      justification?: string;
    }) => {
      setProcessingId(handReceiptId);
      let da4949Window: Window | null = null;
      try {
        const row = allocationRows.find((entry) => entry.id === handReceiptId);
        const idsToUpdate = row ? row.itemIds : [handReceiptId];
        da4949Window = preOpenWindow();

        if (idsToUpdate.length === 1) {
          // Single item — use the original endpoint (generates DA4949 inline)
          const result = await convertAllocationToSizedAsset.mutateAsync({
            handReceiptId: idsToUpdate[0]!,
            targetCatalogId,
            performedBy: performerId,
            justification,
          });
          if (result?.da4949GeneratedDocumentId) {
            await openDocumentWithAuth(
              `/api/v1/documents/${result.da4949GeneratedDocumentId}`,
              da4949Window,
            );
          } else if (da4949Window && !da4949Window.closed) {
            da4949Window.close();
          }
        } else {
          // Multiple items — use batch endpoint (one consolidated DA4949)
          const batchResult = await convertAllocationToSizedAssetBatch.mutateAsync({
            handReceiptIds: idsToUpdate,
            targetCatalogId,
            performedBy: performerId,
            justification,
          });
          if (batchResult?.da4949GeneratedDocumentId) {
            await openDocumentWithAuth(
              `/api/v1/documents/${batchResult.da4949GeneratedDocumentId}`,
              da4949Window,
            );
          } else if (da4949Window && !da4949Window.closed) {
            da4949Window.close();
          }
        }
      } catch (error) {
        if (da4949Window && !da4949Window.closed) {
          da4949Window.close();
        }
        throw error;
      } finally {
        setProcessingId(null);
      }
    },
    [allocationRows, convertAllocationToSizedAsset, convertAllocationToSizedAssetBatch, performerId],
  );

  const allocationColumns = useMemo(
    () =>
      createAllocationColumns({
        onUpdate: handleOpenUpdate,
        isRowUpdating: (id) => processingId === id,
        hideUpdate: isViewer,
      }, ieKitLabel),
    [handleOpenUpdate, processingId, isViewer, ieKitLabel],
  );

  const selectorOpen = Boolean(columnSelectorAnchor);
  const selectorId = selectorOpen ? 'front-window-column-selector' : undefined;

  const handleReceiptFilterChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      updateAllocationPrefs({ receiptFilter: event.target.value as ReceiptFilter });
    },
    [updateAllocationPrefs],
  );

  const handleTypeFiltersChange = useCallback(
    (_event: React.SyntheticEvent, value: CatalogType[]) => {
      updateAllocationPrefs({ typeFilters: value });
    },
    [updateAllocationPrefs],
  );

  const handleRecoverableFilterChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      updateAllocationPrefs({ recoverableFilter: event.target.value as RecoverableFilter });
    },
    [updateAllocationPrefs],
  );

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      updateAllocationPrefs({ search: event.target.value });
    },
    [updateAllocationPrefs],
  );

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      const nextSortModel = model.length > 0 ? model : getDefaultAllocationSortModel();
      updateAllocationPrefs({ sortModel: nextSortModel });
    },
    [updateAllocationPrefs],
  );

  const paginationModel = useMemo(
    () => ({ pageSize: allocationPrefs.pageSize, page: 0 }),
    [allocationPrefs.pageSize],
  );

  const handlePaginationModelChange = useCallback(
    (model: { pageSize: number; page: number }) => {
      updateAllocationPrefs({ pageSize: model.pageSize });
    },
    [updateAllocationPrefs],
  );

  const handleToggleColumn = useCallback(
    (field: AllocationManageableField) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const next = useColumnPreferencesStore
        .getState()
        .updateColumn(
          effectiveUserId,
          FRONT_WINDOW_ALLOCATION_PREF_KEY,
          field,
          event.target.checked,
        );
      const sanitized = buildAllocationVisibilityModel(next);
      setColumnVisibilityModel(sanitized);
      updateAllocationPrefs({ columnVisibility: sanitized });
    },
    [effectiveUserId, updateAllocationPrefs],
  );

  const handleVisibilityModelChange = useCallback(
    (model: GridColumnVisibilityModel) => {
      const sanitized = buildAllocationVisibilityModel(model as ColumnVisibilityModel);
      setColumnVisibilityModel((prev) => {
        if (allocationModelsEqual(prev, sanitized)) {
          return prev;
        }
        const sanitizedForStore = ALLOCATION_MANAGEABLE_COLUMNS.reduce<ColumnVisibilityModel>(
          (acc, column) => {
            acc[column.field] = sanitized[column.field] ?? true;
            return acc;
          },
          {} as ColumnVisibilityModel,
        );
        useColumnPreferencesStore
          .getState()
          .setVisibility(effectiveUserId, FRONT_WINDOW_ALLOCATION_PREF_KEY, sanitizedForStore);
        return sanitized;
      });
    },
    [effectiveUserId],
  );

  const ieItems = useMemo(() => ieData ?? [], [ieData]);
  const iePending = Boolean(personId && !ieFetched && ieLoading);

  const totalAssets = useMemo(() => {
    const phrCount = (phrItems ?? []).length;
    const thrCount = (thrItems ?? []).reduce((sum, item) => sum + (item.qty ?? 0), 0);
    return { phrCount, thrCount, total: phrCount + thrCount };
  }, [phrItems, thrItems]);

  const totalShowing = useMemo(() => {
    return allocationRows.reduce((sum, row) => sum + row.quantity, 0);
  }, [allocationRows]);

  const handlePrintAllocation = useCallback(async () => {
    if (printingRef.current) return;

    if (!currentCustomer) {
      notifyError(`Select ${person.article} ${person.lower}`, `Choose ${person.article} ${person.lower} before printing a summary.`);
      return;
    }

    if (allocationRows.length === 0) {
      notifyWarning('Nothing to print', 'No authorized or issued items are visible to print.');
      return;
    }

    printingRef.current = true;
    setIsPrinting(true);

    const heading = resolvePrintHeading(receiptFilter, recoverableFilter, typeFilters);
    const filtersSummary = buildFiltersSummary(
      receiptFilter,
      recoverableFilter,
      typeFilters,
      search,
    );

    const memberInfo: AllocationSummaryMemberInfo = {
      // Build name from first/last to avoid duplicating rank already embedded in display_name
      name:
        [currentCustomer.first_name, currentCustomer.last_name].filter(Boolean).join(' ').trim() ||
        currentCustomer.display_name,
      rank: currentCustomer.rank,
      unit: currentCustomer.unit,
      individualId: currentCustomer.individual_id,
    };

    try {
      await runAllocationSummaryReport({
        personId,
        phrItems: phrItems ?? [],
        thrItems: thrItems ?? [],
        ieItems,
        member: memberInfo,
        receiptFilter,
        recoverableFilter,
        typeFilters,
        search,
        heading,
        filtersSummary,
      });
    } catch (err) {
      notifyError(
        'Print failed',
        err instanceof Error ? err.message : 'Unable to generate allocation summary.',
      );
    } finally {
      // Small delay to prevent accidental double-clicks from triggering again immediately
      setTimeout(() => {
        printingRef.current = false;
        setIsPrinting(false);
      }, 500);
    }
  }, [
    allocationRows,
    currentCustomer,
    notifyError,
    notifyWarning,
    personId,
    phrItems,
    thrItems,
    ieItems,
    receiptFilter,
    recoverableFilter,
    search,
    typeFilters,
  ]);

  const isLoading = phrLoading || thrLoading;
  const showEmpty = !isLoading && phrFetched && thrFetched && allocationRows.length === 0;
  const printDisabled = isLoading || allocationRows.length === 0 || isPrinting;

  // Register action buttons in tab header - use refs to avoid re-render loops
  const handlePrintRef = useRef(handlePrintAllocation);
  handlePrintRef.current = handlePrintAllocation;

  // Memoize the action buttons to prevent infinite re-render loops
  const actionButtons = useMemo(() => {
    if (!currentCustomer) return null;

    return (
      <>
        <Button
          size="small"
          variant="outlined"
          startIcon={<ViewColumnIcon fontSize="small" />}
          onClick={(event) => setColumnSelectorAnchor(event.currentTarget)}
        >
          Manage Columns
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={<PrintIcon fontSize="small" />}
          onClick={() => handlePrintRef.current()}
          disabled={printDisabled}
        >
          Print Summary
        </Button>
      </>
    );
  }, [currentCustomer, printDisabled]);

  useEffect(() => {
    if (!tabActions) return;
    tabActions.setActions(actionButtons);
    return () => tabActions.setActions(null);
  }, [tabActions, actionButtons]);

  if (!currentCustomer) {
    return (
      <Alert severity="info">
        Select {person.article} {person.lower} to view allocation details and take action on hand receipts.
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        '& .unauthorized-row': {
          bgcolor: (t) => t.palette.mode === 'dark'
            ? 'rgba(244, 194, 101, 0.20)'
            : '#F4C265',
          '&:hover': {
            bgcolor: (t) => t.palette.mode === 'dark'
              ? 'rgba(244, 194, 101, 0.30)'
              : '#F0B64D',
          },
        },
      }}
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            value={search}
            onChange={handleSearchChange}
            size="small"
            placeholder="Search allocations"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 200 }}
          />
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              px: 0.75,
              py: 0.125,
            }}
          >
            <RadioGroup
              row
              value={receiptFilter}
              onChange={handleReceiptFilterChange}
              aria-label="Hand receipt filter"
              sx={{ flexWrap: 'nowrap' }}
            >
              <FormControlLabel value="all" control={<Radio size="small" />} label="All" />
              <FormControlLabel value="phr" control={<Radio size="small" />} label="PHR" />
              <FormControlLabel value="thr" control={<Radio size="small" />} label="THR" />
            </RadioGroup>
          </Box>
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              px: 0.75,
              py: 0.125,
            }}
          >
            <RadioGroup
              row
              value={recoverableFilter}
              onChange={handleRecoverableFilterChange}
              aria-label="Recoverable filter"
              sx={{ flexWrap: 'nowrap' }}
            >
              <FormControlLabel value="all" control={<Radio size="small" />} label="All" />
              <FormControlLabel
                value="recoverable"
                control={<Radio size="small" />}
                label="Recov"
              />
              <FormControlLabel
                value="nonRecoverable"
                control={<Radio size="small" />}
                label="Non-Recov"
              />
            </RadioGroup>
          </Box>
          <Autocomplete
            multiple
            size="small"
            options={['Buy Program', 'PnP', 'Stock'] as CatalogType[]}
            value={typeFilters}
            onChange={handleTypeFiltersChange}
            renderInput={(params) => (
              <TextField {...params} placeholder="Filter by Type" size="small" />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return <Chip key={key} label={option} size="small" {...tagProps} />;
              })
            }
            sx={{
              minWidth: 200,
              '& .MuiOutlinedInput-root': {
                py: 0.375,
              },
            }}
          />
        </Stack>
      </Stack>

      <Typography variant="body2" color="text.secondary">
        Showing {totalShowing} of {totalAssets.total} assigned items (PHR {totalAssets.phrCount}
        {' / '}THR {totalAssets.thrCount})
      </Typography>

      {phrError && !phrErrorDismissed && (
        <Alert severity="error" onClose={() => setPhrErrorDismissed(true)}>
          {phrError instanceof Error ? phrError.message : 'Failed to load PHR assignments'}
        </Alert>
      )}

      {thrError && !thrErrorDismissed && (
        <Alert severity="error" onClose={() => setThrErrorDismissed(true)}>
          {thrError instanceof Error ? thrError.message : 'Failed to load THR assignments'}
        </Alert>
      )}

      {isLoading && <TableSkeleton aria-label="Loading allocation items" />}

      {showEmpty && (
        <Alert severity="info">No assets currently allocated to this {person.lower}.</Alert>
      )}

      {!isLoading && allocationRows.length > 0 && (
        <div ref={gridWrapperRef} style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <DataGrid
            density="compact"
            rows={allocationRows}
            columns={allocationColumns}
            disableColumnMenu
            sortingOrder={['asc', 'desc']}
            isRowSelectable={() => false}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            paginationModel={paginationModel}
            onPaginationModelChange={handlePaginationModelChange}
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={handleVisibilityModelChange}
            getRowClassName={(params) => (params.row.unauthorized ? 'unauthorized-row' : '')}
            autoHeight
            sx={{
              '& .MuiDataGrid-topContainer': {
                zIndex: 2,
                bgcolor: 'background.paper',
              },
            }}
          />
        </div>
      )}

      <UpdateThrItemDialog
        open={dialogOpen}
        item={selectedAllocation}
        onClose={handleCloseDialog}
        onExtend={handleExtend}
        onConvert={handleConvert}
        onConvertToSizedAsset={handleConvertToSizedAsset}
        ieItems={ieItems}
        sizeVariants={sizeVariantsForDialog}
        isExtendPending={extendThrDueDate.isPending}
        isConvertPending={convertThrToPhr.isPending}
        isConvertToSizedPending={convertAllocationToSizedAsset.isPending || convertAllocationToSizedAssetBatch.isPending}
        isIePending={iePending}
        isSizeVariantsPending={selectedCatalogChildrenLoading}
      />

      <Popover
        id={selectorId}
        open={selectorOpen}
        anchorEl={columnSelectorAnchor}
        onClose={() => setColumnSelectorAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { p: 2, width: 240 } } }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Visible Columns
        </Typography>
        <FormGroup>
          {ALLOCATION_MANAGEABLE_COLUMNS.map(({ field, label }) => (
            <FormControlLabel
              key={field}
              control={
                <Checkbox
                  size="small"
                  checked={columnVisibilityModel[field] !== false}
                  onChange={handleToggleColumn(field)}
                />
              }
              label={label}
            />
          ))}
        </FormGroup>
      </Popover>
    </Box>
  );
};
